import React from 'react';

import { ActionIcon, Select, Space, TextInput, useMantineTheme } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconSearch, IconX } from '@tabler/icons-react';
import ProductMultiSelect from 'components/form-fields/auto-complete-fields/ProductMultiSelectAutoComplete';
import InvoiceStatusMultiSelect from 'components/form-fields/select-fields/InvoiceStatusMultiSelect';
import PaymentMethodMultiSelect from 'components/form-fields/select-fields/PaymentMethodMultiSelect';
import { InvoicesTableSearchFilters } from 'components/invoices/InvoicesTable';
import { useLocalization } from 'context/localization-context';
import { InvoiceFilterSource, useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { Product } from 'types';

/**
 * Every filter the invoice tables can offer. A view lists the ones that apply;
 * anything omitted is simply not rendered — the four dashboard reports hide
 * `status` because they hard-filter invoice status behind the scenes.
 */
export type InvoiceFilterKey =
  | 'search'
  | 'dueDateRange'
  | 'paymentDateRange'
  | 'status'
  | 'productType'
  | 'billingSources'
  | 'autoPay'
  | 'paymentMethods';

export interface InvoiceFilterControlsProps {
  /** Controls to render, in display order. Payment Method conventionally goes last. */
  filters: InvoiceFilterKey[];
  searchFilters: InvoicesTableSearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<InvoicesTableSearchFilters>>;
  searchPlaceholder?: string;
  /** Which view this bar belongs to — reported with every filter event. */
  source: InvoiceFilterSource;
  /** Upcoming Revenue projects forward only, and keeps a range always applied. */
  dueDateMinDate?: Date;
  dueDateClearable?: boolean;
}

const PRODUCT_TYPE_OPTIONS = [
  { value: 'PACKAGE', label: 'Package', 'data-cy': 'packageOption' },
  { value: 'RETAIL', label: 'Retail', 'data-cy': 'retailOption' },
  { value: 'FEES AND MISCELLANEOUS', label: 'Fees and Misc', 'data-cy': 'feesMiscOption' },
  {
    value: 'CANCELLATION AND NO SHOW FEE',
    label: 'Late Cancel / No Show',
    'data-cy': 'lateCancelNoShowOption',
  },
];

const AUTO_PAY_OPTIONS = [
  { value: 'True', label: 'On' },
  { value: 'False', label: 'Off' },
];

/**
 * The invoice filter set, without layout.
 *
 * Split out from InvoiceFilterBar so the staff mobile view can render the same
 * controls inside its bottom drawer. The drawer is a different interaction, not
 * a breakpoint of the desktop bar, so the two share the filter set and its
 * behaviour rather than one layout serving both. Adding a filter here reaches
 * every invoice view at once, which was the point of standardizing.
 */
const InvoiceFilterControls = ({
  filters,
  searchFilters,
  setSearchFilters,
  searchPlaceholder = 'Search by product or athlete',
  source,
  dueDateMinDate,
  dueDateClearable = true,
}: InvoiceFilterControlsProps): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();
  const { trackInvoiceFilterApplied } = useStaffTracking();

  const shows = (filter: InvoiceFilterKey): boolean => filters.includes(filter);

  const updateFilter = <K extends keyof InvoicesTableSearchFilters>(
    key: K,
    value: InvoicesTableSearchFilters[K],
  ): void => setSearchFilters(prev => ({ ...prev, [key]: value }));

  // No end-of-day adjustment: formatDateForApi sends YYYY-MM-DD, and the
  // backend's DateToDateTimeFilter(end_of_day=True) already expands the end of
  // the range to 23:59:59.
  const handleDateRangeChange =
    (key: 'dueDateRange' | 'paymentDateRange') =>
    (value: DatesRangeValue): void =>
      updateFilter(key, value);

  return (
    <>
      {shows('search') && (
        <TextInput
          icon={<IconSearch size={theme.fontSizes.md} />}
          placeholder={searchPlaceholder}
          onChange={e => updateFilter('search', e.currentTarget?.value || '')}
          value={searchFilters.search}
          data-cy='invoiceSearchInput'
          rightSection={
            !!searchFilters.search ? (
              <ActionIcon onClick={() => updateFilter('search', '')} size='md'>
                <IconX size={theme.fontSizes.md} />
              </ActionIcon>
            ) : (
              <Space />
            )
          }
        />
      )}

      {shows('dueDateRange') && (
        <DatePickerInput
          value={searchFilters.dueDateRange}
          onChange={handleDateRangeChange('dueDateRange')}
          valueFormat={dateFormat}
          type='range'
          clearable={dueDateClearable}
          minDate={dueDateMinDate}
          allowSingleDateInRange
          data-cy='invoiceDueDateRangeInput'
          {...{ placeholder: 'Select due date range' }}
        />
      )}

      {shows('paymentDateRange') && (
        <DatePickerInput
          value={searchFilters.paymentDateRange}
          onChange={handleDateRangeChange('paymentDateRange')}
          valueFormat={dateFormat}
          type='range'
          clearable
          allowSingleDateInRange
          data-cy='invoicePaymentDateRangeInput'
          {...{ placeholder: 'Select payment date range' }}
        />
      )}

      {shows('status') && (
        <InvoiceStatusMultiSelect
          placeholder='Select invoice status'
          value={searchFilters.status}
          onChange={value => updateFilter('status', value)}
          clearable
          data-cy='invoiceStatusInput'
        />
      )}

      {shows('productType') && (
        <Select
          placeholder='Select product type'
          data={PRODUCT_TYPE_OPTIONS}
          value={searchFilters.productType}
          onChange={value =>
            // Billing sources are products of the selected type, so changing the
            // type invalidates whatever was picked. The report bars did this too.
            setSearchFilters(prev =>
              prev.productType !== value
                ? { ...prev, productType: value, billingSources: [] }
                : { ...prev, productType: value },
            )
          }
          clearable
          data-cy='productTypeInput'
        />
      )}

      {shows('billingSources') && (
        <ProductMultiSelect
          placeholder='Select Billing Sources'
          value={searchFilters.billingSources}
          onChange={(value: string[] | undefined) => updateFilter('billingSources', value || [])}
          type={searchFilters.productType as Product['type']}
          data-cy='billingSourcesInput'
        />
      )}

      {shows('autoPay') && (
        <Select
          placeholder='Select autopay status'
          data={AUTO_PAY_OPTIONS}
          value={searchFilters.autoPay}
          onChange={value => updateFilter('autoPay', value)}
          clearable
          data-cy='autoPayInput'
        />
      )}

      {shows('paymentMethods') && (
        <PaymentMethodMultiSelect
          placeholder='Select payment method'
          value={searchFilters.paymentMethods}
          onChange={value => {
            updateFilter('paymentMethods', value);
            // 'none' rather than an empty string, so clearing the filter is a
            // countable action instead of a gap in the data.
            trackInvoiceFilterApplied('payment_method', value.join(',') || 'none', source);
          }}
          clearable
          data-cy='paymentMethodInput'
        />
      )}
    </>
  );
};

export default InvoiceFilterControls;
